import React, { useState, useEffect, useRef } from 'react';
import { Box, Select } from "@chakra-ui/react";
import { AuthContext } from "contexts/AuthContext";
import { useHistory } from 'react-router-dom';
import { axiosInstance } from "api/apiClient";
import LoadingSpinner from '../../../components/spinner/spinner';
import ExpiredTokenModal from 'components/expiredTokenModal/expiredTokenModal';
import './sandbox.css';

export default function SandBox() {
  const { userInfos, signOut } = React.useContext(AuthContext);
  const history = useHistory();
  const token = localStorage.getItem("jwtToken");

  const [assistants, setAssistants] = useState([]);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [tokenExpModalOpen, setTokenExpModalOpen] = useState(false);

  const sandboxRef = useRef(null);

  useEffect(() => {
    if (!userInfos || !userInfos.client_id) {
      signOut();
      history.push("/auth");
      return;
    }

    let isMounted = true;

    const fetchAssistants = async () => {
      try {
        const response = await axiosInstance.get(`assistant/get_client_assistants?client_id=${userInfos.client_id}`);
        if (isMounted) {
          setAssistants(response.data);
          setLoadingDoc(false);
          if (response.data.length > 0) {
            const defaultAssistant = response.data[0];
            setSelectedAssistant(defaultAssistant);
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          setTokenExpModalOpen(true);
          setLoadingDoc(false);
        } else {
          console.error("Error fetching assistants:", error);
          if (isMounted) {
            setLoadingDoc(false);
          }
        }
      }
    };

    fetchAssistants();

    return () => {
      isMounted = false;
    };
  }, [userInfos]);

  const handleAssistantChange = (event) => {
    const selectedId = event.target.value;
    const selectedAssistant = assistants.find(assistant => assistant.assistant_id === selectedId);
    if (selectedAssistant) {
      setSelectedAssistant(selectedAssistant);
    }
  };

  const handleTokenExpModalConfirm = () => {
    setTokenExpModalOpen(false);
    signOut();
    history.push("/auth");
  };

  useEffect(() => {
    if (sandboxRef.current) {
      const scriptContent = document.createElement('script');
      scriptContent.type = 'text/javascript';
      scriptContent.innerHTML = `
        var DOCUMENT_ID = '${selectedAssistant ? selectedAssistant.assistant_id : ''}';
        var VF_ID = '${selectedAssistant ? selectedAssistant.vf_id : ''}';
      `;

      const scriptSrc = document.createElement('script');
      scriptSrc.src = "https://storage.googleapis.com/capture-cdn/onsite-js/message_selector.js";
      scriptSrc.async = true;

      sandboxRef.current.appendChild(scriptContent);
      sandboxRef.current.appendChild(scriptSrc);

      return () => {
        if (sandboxRef.current) {
          sandboxRef.current.removeChild(scriptContent);
          sandboxRef.current.removeChild(scriptSrc);
        }
        const voiceflowChat = document.getElementById('voiceflow-chat');
        if (voiceflowChat) {
          voiceflowChat.remove();
          const scriptElements = document.querySelectorAll('script[src="https://storage.googleapis.com/capture-cdn/onsite-js/message_selector.js"]');
          scriptElements.forEach(script => script.remove());
          delete window.voiceflow;
        }
      };
    }
  }, [selectedAssistant]);

  if (loadingDoc) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      pt={{ base: "180px", md: "80px", xl: "80px" }}
      px={0}
      height="calc(100vh - 80px)"
      position="relative"
    >
      <Select
        maxWidth="30%"
        onChange={handleAssistantChange}
        value={selectedAssistant ? selectedAssistant.assistant_id : ''}
        mb={4}
      >
        {assistants.map(assistant => (
          <option key={assistant.assistant_id} value={assistant.assistant_id}>{assistant.name}</option>
        ))}
      </Select>

      <div
        ref={sandboxRef}
        className="sandbox-main-content" 
        style={{ 
          position: 'relative', 
          height: "90%", 
          top: "20px", 
          left: 0, 
          right: 0, 
          bottom: 0
        }}
      >
        <div className="sandbox-html-content">
          <nav className="navbar">
            <div className="logo">
              <i className="fas fa-robot"></i>
              Démo Site
            </div>
            <div className="nav-links">
              <a>Accueil</a>
              <a>Services</a>
              <a>Contact</a>
            </div>
          </nav>

          <div className="hero">
            <h1>La manière de régler votre problème</h1>
            <p>Découvrez nos services et solutions innovantes</p>
            <a href="#contact" className="cta-button">PLANIFIER UNE RENCONTRE</a>
          </div>

          <section className="features">
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-comments"></i>
                </div>
                <h3>Assistant IA 24/7</h3>
                <p>Un agent conversationnel intelligent disponible en tout temps pour vos clients.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3>Conversion Optimale</h3>
                <p>Augmentez vos taux de conversion grâce à l'intelligence artificielle.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-calendar-check"></i>
                </div>
                <h3>Rencontres Automatisées</h3>
                <p>Planification automatique des rendez-vous avec les prospects qualifiés.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
      <ExpiredTokenModal isOpen={tokenExpModalOpen} onClose={handleTokenExpModalConfirm} />
    </Box>
  );
};
